import { Arrow, Figure, INK_FAINT, INK_MUTED, Lane, Node, Note, Pill } from './primitives';

/**
 * One automation run, from the thing that fired it to the document it delivers.
 *
 * The eight triggers are drawn as two groups converging on one function, because that convergence is
 * the design: four scheduled kinds are not four schedulers, and every path — including Run now —
 * funnels through `createAndEnqueue` so a run row always exists before a job references it.
 */
export function AutomationPath() {
  return (
    <Figure
      title="An automation run, from trigger to delivered document"
      width={760}
      height={430}
      caption="Delivery sits outside the loop deliberately: the agent cannot forget to post, and a Slack outage cannot cost the document — the run still completes, with the failure recorded beside it."
    >
      <Note x={0} y={14} lines="TRIGGERS" size={9} weight={700} color={INK_FAINT} />
      <Lane x={0} y={24} w={196} h={112} label="scheduler tick · 1/min" />
      <Node x={12} y={44} w={172} h={20} title="DAILY_AT_TIME" tone="muted" mono />
      <Node x={12} y={68} w={172} h={20} title="WEEKLY_ON_DAYS" tone="muted" mono />
      <Node x={12} y={92} w={172} h={20} title="MONTHLY_ON_DATE" tone="muted" mono />
      <Node x={12} y={116} w={172} h={14} title="ONCE_AT_DATETIME" tone="muted" mono />

      <Lane x={0} y={152} w={196} h={112} label="events" />
      <Node x={12} y={172} w={172} h={20} title="GRANOLA·SLACK·EMAIL·DRIVE" tone="muted" mono />
      <Node x={12} y={196} w={172} h={20} title="CALENDAR · before / after" tone="muted" mono />
      <Node x={12} y={220} w={172} h={20} title="KB_RUN_COMPLETED" tone="muted" mono />
      <Node x={12} y={244} w={172} h={14} title="Run now" tone="accent" />

      <Note x={0} y={282} lines={['one question:', 'previousOccurrence', 'vs lastTriggeredAt']} size={9} mono />

      <Arrow points={[[196, 80], [232, 80], [232, 146]]} />
      <Arrow points={[[196, 206], [232, 206], [232, 166]]} />

      <Node x={244} y={132} w={176} h={48} title="createAndEnqueue" sub="the row exists first" tone="default" mono />
      <Pill x={276} y={190} label="automation-runs · 2" tone="muted" />

      <Arrow points={[[420, 156], [462, 156]]} />

      <Lane x={462} y={20} w={298} h={218} label="one loop · fixed roster" tone="accent" />
      <Node x={478} y={44} w={266} h={24} title="8 context tools" tone="muted" />
      <Node x={478} y={74} w={266} h={24} title="11 source tools" tone="muted" />
      <Node x={478} y={104} w={266} h={24} title="4 record tools" tone="info" />
      <Node x={478} y={134} w={266} h={24} title="2 actions — conditional" tone="accent" dashed />
      <Node x={478} y={172} w={266} h={28} title="finish(output)" tone="success" mono />
      <Arrow points={[[611, 158], [611, 172]]} />

      <Note x={600} y={256} anchor="end" lines="16 always offered; the 2 actions are conditional" size={9} color={INK_MUTED} />

      <Arrow points={[[611, 238], [611, 284]]} />
      <Node x={500} y={284} w={222} h={44} title="Google Drive" sub="markdown → native Doc" tone="info" />
      <Arrow points={[[500, 306], [420, 306]]} label="carries its link" labelAt={[460, 300]} />
      <Node x={198} y={284} w={222} h={44} title="Slack" sub="markdown block + mrkdwn text" tone="info" />

      <Arrow points={[[309, 328], [309, 356]]} />
      <Arrow points={[[611, 328], [611, 344], [420, 344], [420, 356]]} head={false} />
      <Node x={198} y={356} w={370} h={44} title="markRunCompleted" sub="output · steps · postedToSlack · driveFileUrl" tone="success" mono />

      <Note
        x={722}
        y={352}
        anchor="end"
        lines={['Drive first, so a Drive outage', 'downgrades the post rather', 'than losing it']}
        size={9}
      />
    </Figure>
  );
}

/**
 * What one document turns into on the way out.
 *
 * Drawn because the two Slack fields are the part people get wrong: the block and the text are not
 * alternatives, they are the same message in two dialects, and sending the document raw into `text`
 * is what put unrendered markup in the channel.
 */
export function AutomationDelivery() {
  return (
    <Figure
      title="How one document becomes a Doc and a Slack message"
      width={760}
      height={296}
      minWidth={620}
      caption="A message needs both fields. With blocks present, `text` is the notification and the fallback rendition — so the document goes in the markdown block and a down-conversion goes in the text."
    >
      <Node x={280} y={8} w={200} h={30} title="finish(output)" tone="accent" mono />
      <Arrow points={[[380, 38], [380, 58]]} />

      <Node x={236} y={58} w={288} h={44} title="upload text/markdown" sub="target mimeType: …google-apps.document" tone="info" mono />
      <Note x={536} y={74} lines={['Drive does the conversion,', 'as Slack does the rendering']} />

      <Arrow points={[[380, 102], [380, 128]]} label="url" labelAt={[386, 120]} labelAnchor="start" />
      <Arrow points={[[236, 80], [100, 80], [100, 196]]} dashed label="driveError" labelAt={[106, 140]} labelAnchor="start" />
      <Note x={60} y={212} lines={['recorded,', 'never fatal']} />

      {/* Both fields inside one lane: they are the same message in two dialects, not alternatives. */}
      <Lane x={168} y={128} w={592} h={96} label="one Slack message" />
      <Node x={184} y={150} w={360} h={28} title="blocks: [{ type: 'markdown' }]" tone="success" mono />
      <Note x={184} y={194} lines={['the document, standard Markdown', 'cap 12,000 chars across all blocks']} />

      <Node x={560} y={150} w={186} h={28} title="text: mrkdwn" tone="muted" mono />
      <Note x={560} y={194} lines={['the notification and', 'the fallback rendition']} />

      <Note
        x={0}
        y={252}
        lines={[
          'Code spans are masked before the inline rules run, so a shell snippet is not rewritten as emphasis.',
          'Splitting is fence-aware — a code block cut in two leaves unbalanced backticks in both halves.',
        ]}
      />
    </Figure>
  );
}
